import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Calendar, MapPin, Video, ArrowRight, Shield, AlertCircle, Lock, LogOut, Eye, EyeOff, Link2, Check } from 'lucide-react';
import { verifyClientCredentials, sendClientSetupEmail, updateEventPrivacy, requestClientPasswordReset } from '../services/emailService';
import { validateEmail } from '../utils/validators';
import LoadingSpinner from '../components/shared/LoadingSpinner';
import stoneGoldBg from '../assets/images/stone-gold-black-bg.png';

function ClientPortalPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPasswordStep, setShowPasswordStep] = useState(false);
  const [events, setEvents] = useState(null);
  const [lookupEmail, setLookupEmail] = useState('');
  const [sessionToken, setSessionToken] = useState(null);
  const [setupEmailSent, setSetupEmailSent] = useState(false);
  const [sendingSetup, setSendingSetup] = useState(null); // eventId being sent
  const [lastSentEventId, setLastSentEventId] = useState(null); // Track which event was just sent
  const [isResending, setIsResending] = useState(false);
  const [updatingPrivacy, setUpdatingPrivacy] = useState(null); // eventId being updated
  const [copiedLinkEventId, setCopiedLinkEventId] = useState(null);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();

    const trimmed = email.trim();
    if (!trimmed) {
      setEmailError('Please enter your email address');
      return;
    }
    if (!validateEmail(trimmed)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    // Email is valid - show password step
    setEmailError('');
    setShowPasswordStep(true);
    setLookupEmail(trimmed);
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (!password) {
      setPasswordError('Please enter your password');
      return;
    }

    setIsLoading(true);
    setPasswordError('');
    setEmailError('');

    const result = await verifyClientCredentials(lookupEmail, password);

    if (result.success) {
      setEvents(result.events);
      setSessionToken(result.sessionToken);

      // Store secure session data
      sessionStorage.setItem('client_session', JSON.stringify({
        token: result.sessionToken,
        email: result.email,
        expiry: result.sessionExpiry
      }));
    } else {
      setPasswordError(result.error || 'Invalid credentials. Please try again.');
    }

    setIsLoading(false);
  };

  const handleBack = () => {
    setEvents(null);
    setLookupEmail('');
    setPassword('');
    setShowPasswordStep(false);
    setSessionToken(null);
    setSetupEmailSent(false);
    setPasswordError('');
    sessionStorage.removeItem('client_session');
  };

  const handleEventClick = async (event) => {
    // If client has already set up password, navigate to gallery with session token
    if (event.clientSetupStatus === 'active' && sessionToken) {
      // Store secure client session for gallery access
      sessionStorage.setItem(`client_auth_${event.id}`, JSON.stringify({
        eventId: event.id,
        email: lookupEmail,
        token: sessionToken,
        timestamp: Date.now()
      }));
      navigate(`/gallery/${event.id}`);
      return;
    }

    // Otherwise, send setup email
    setSendingSetup(event.id);
    const result = await sendClientSetupEmail(event.id);

    if (result.success) {
      setLastSentEventId(event.id);
      setSetupEmailSent(true);
    } else {
      alert(result.error || 'Failed to send setup email. Please try again.');
    }

    setSendingSetup(null);
  };

  const handleResendEmail = async () => {
    if (!lastSentEventId || isResending) return;

    setIsResending(true);
    const result = await sendClientSetupEmail(lastSentEventId);

    if (result.success) {
      alert('Setup email resent! Please check your inbox and spam folder.');
    } else {
      alert(result.error || 'Failed to resend email. Please try again.');
    }

    setIsResending(false);
  };

  const handleForgotPassword = async () => {
    if (sendingReset) return;
    setSendingReset(true);
    setPasswordError('');

    await requestClientPasswordReset(lookupEmail);

    // Always show success (prevents email enumeration)
    setSendingReset(false);
    setResetEmailSent(true);
  };

  const handleBackToEmail = () => {
    setResetEmailSent(false);
    setShowPasswordStep(false);
    setPassword('');
    setPasswordError('');
  };

  const handleTogglePrivacy = async (event, e) => {
    // Stop propagation to prevent event card click
    e.stopPropagation();

    if (updatingPrivacy === event.id) return; // Already updating

    setUpdatingPrivacy(event.id);

    const newPrivacySetting = !event.isPublic;
    const result = await updateEventPrivacy(
      event.id,
      lookupEmail,
      password,
      newPrivacySetting
    );

    if (result.success) {
      // Update local state to reflect the change
      setEvents(prevEvents =>
        prevEvents.map(evt =>
          evt.id === event.id
            ? { ...evt, isPublic: newPrivacySetting }
            : evt
        )
      );
    } else {
      alert(result.error || 'Failed to update privacy setting. Please try again.');
    }

    setUpdatingPrivacy(null);
  };

  const handleCopyPublicLink = async (event, e) => {
    e.stopPropagation();

    const galleryUrl = `${window.location.origin}/gallery/${event.id}`;

    try {
      await navigator.clipboard.writeText(galleryUrl);
      setCopiedLinkEventId(event.id);
      setTimeout(() => setCopiedLinkEventId(null), 2000);
      return;
    } catch (err) {
      console.error('Clipboard API copy failed:', err);
    }

    try {
      const textarea = document.createElement('textarea');
      textarea.value = galleryUrl;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'absolute';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      const copied = document.execCommand('copy');
      document.body.removeChild(textarea);

      if (copied) {
        setCopiedLinkEventId(event.id);
        setTimeout(() => setCopiedLinkEventId(null), 2000);
      } else {
        alert(`Copy failed. Please copy this link manually:\n${galleryUrl}`);
      }
    } catch (fallbackErr) {
      console.error('Fallback copy failed:', fallbackErr);
      alert(`Copy failed. Please copy this link manually:\n${galleryUrl}`);
    }
  };

  const formatEventDate = (timestamp) => {
    if (!timestamp) return '';
    // Cloud Function returns Firestore timestamp as {_seconds, _nanoseconds}
    const date = timestamp._seconds
      ? new Date(timestamp._seconds * 1000)
      : new Date(timestamp);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  };

  return (
    <div
      className="min-h-screen flex flex-col bg-cover bg-center bg-fixed"
      style={{
        backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.85), rgba(0,0,0,0.75)), url(${stoneGoldBg})`
      }}
    >
      {/* Public Navbar - Login View */}
      {events === null && (
        <div className="sticky top-0 z-30 bg-dark-slate/85 backdrop-blur-md border-b border-white/10">
          <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
            <Link
              to="/"
              className="text-xl font-bold tracking-tight text-white hover:text-gold transition-colors"
            >
              MomentBox
            </Link>
            <span className="text-sm text-gray-400 hidden sm:inline">Digital Guestbook</span>
          </div>
        </div>
      )}

      <div className="max-w-2xl mx-auto w-full p-6">

        {/* Logout button - only show when logged in */}
        {events !== null && (
          <button
            onClick={handleBack}
            className="inline-flex items-center gap-2 text-gray-400 hover:text-gold mb-8 transition-colors"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-medium">Logout</span>
          </button>
        )}

        {/* Email & Password Input State */}
        {events === null && (
          <div className="bg-neutral-900/80 backdrop-blur-sm border border-white/10 rounded-2xl p-8 shadow-2xl">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-6">
                {showPasswordStep ? (
                  <Lock className="w-10 h-10 text-gold" />
                ) : (
                  <Shield className="w-10 h-10 text-gold" />
                )}
              </div>
              <h1 className="text-4xl font-bold text-white mb-3">
                Client Portal
              </h1>
              <p className="text-neutral-grey-300 text-lg">
                {showPasswordStep
                  ? 'Enter your password to continue'
                  : 'Enter your email to access your event galleries'
                }
              </p>
            </div>

            {!showPasswordStep ? (
              <form onSubmit={handleEmailSubmit} className="space-y-6">
              <div>
                <label htmlFor="clientEmail" className="block text-white font-medium mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  id="clientEmail"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setEmailError('');
                  }}
                  className={`w-full px-4 py-3 bg-white/10 border-2 ${
                    emailError ? 'border-crimson' : 'border-white/20'
                  } text-white rounded-lg focus:border-gold focus:outline-none placeholder-neutral-grey-400`}
                  placeholder="your.email@example.com"
                  autoFocus
                />
                {emailError && (
                  <p className="mt-2 text-crimson text-sm">{emailError}</p>
                )}
              </div>

              <button
                type="submit"
                className="w-full py-4 bg-gold hover:bg-gold-light text-dark-slate font-bold rounded-lg transition-all text-lg shadow-lg shadow-gold/30"
              >
                Continue
              </button>
            </form>
            ) : resetEmailSent ? (
              <div className="text-center">
                <div className="w-20 h-20 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Mail className="w-10 h-10 text-gold" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-4">Check Your Email</h2>
                <p className="text-neutral-grey-300 mb-6">
                  If an account exists for <strong className="text-white">{lookupEmail}</strong>, we've sent a password reset link.
                </p>
                <p className="text-neutral-grey-400 text-sm mb-8">
                  Check your inbox and spam folder. The link expires in 1 hour.
                </p>
                <button
                  onClick={handleBackToEmail}
                  className="text-gold hover:text-gold-light font-medium transition-colors"
                >
                  Back to login
                </button>
              </div>
            ) : (
              <form onSubmit={handlePasswordSubmit} className="space-y-6">
                <div>
                  <label className="block text-white font-medium mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={lookupEmail}
                    disabled
                    className="w-full px-4 py-3 bg-white/5 border-2 border-white/10 text-white/70 rounded-lg"
                  />
                </div>

                <div>
                  <label htmlFor="clientPassword" className="block text-white font-medium mb-2">
                    Password
                  </label>
                  <input
                    type="password"
                    id="clientPassword"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setPasswordError('');
                    }}
                    className={`w-full px-4 py-3 bg-white/10 border-2 ${
                      passwordError ? 'border-crimson' : 'border-white/20'
                    } text-white rounded-lg focus:border-gold focus:outline-none placeholder-neutral-grey-400`}
                    placeholder="Enter your password"
                    autoFocus
                  />
                  {passwordError && (
                    <p className="mt-2 text-crimson text-sm">{passwordError}</p>
                  )}
                  <div className="mt-2 text-right">
                    <button
                      type="button"
                      onClick={handleForgotPassword}
                      disabled={sendingReset}
                      className="text-gold hover:text-gold-light text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {sendingReset ? 'Sending...' : 'Forgot Password?'}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-4 bg-gold hover:bg-gold-light text-dark-slate font-bold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed text-lg shadow-lg shadow-gold/30"
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <LoadingSpinner size="small" />
                      Verifying...
                    </span>
                  ) : 'Access My Events'}
                </button>
              </form>
            )}
          </div>
        )}

        {/* Events List State */}
        {events !== null && (
          <div>
            <div className="bg-neutral-900/80 backdrop-blur-sm border border-white/10 rounded-2xl p-8 shadow-2xl mb-6">
              <div className="mb-4">
                <h1 className="text-3xl font-bold text-white mb-2">Your Events</h1>
                <div className="flex items-center gap-2 text-neutral-grey-300">
                  <Mail className="w-4 h-4 text-gold" />
                  <span>{lookupEmail}</span>
                </div>
              </div>
            </div>

            {events.length === 0 ? (
              <div className="bg-neutral-900/80 backdrop-blur-sm border border-white/10 rounded-2xl p-12 shadow-2xl text-center">
                <p className="text-neutral-grey-300 text-lg mb-4">
                  No events found for this email address.
                </p>
                <p className="text-neutral-grey-400 text-sm">
                  Make sure you're using the same email your event host used to set up your account.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {events.map((event) => (
                  <button
                    key={event.id}
                    onClick={() => handleEventClick(event)}
                    disabled={sendingSetup === event.id}
                    className="w-full bg-neutral-900/80 backdrop-blur-sm border border-white/10 hover:border-gold/50 rounded-2xl p-6 shadow-2xl text-left transition-all group disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-3">
                          <h2 className="text-xl font-bold text-white truncate">
                            {event.eventName}
                          </h2>
                          <span className={`px-2.5 py-1 rounded-full text-xs font-semibold shrink-0 ${
                            event.isPublic
                              ? 'bg-green-500/20 border border-green-500/40 text-green-400'
                              : 'bg-blue-500/20 border border-blue-500/40 text-blue-400'
                          }`}>
                            {event.isPublic ? 'Public' : 'Private'}
                          </span>
                        </div>

                        <div className="flex flex-wrap items-center gap-4 text-sm text-neutral-grey-300">
                          {event.eventDate && (
                            <div className="flex items-center gap-1.5">
                              <Calendar className="w-4 h-4 text-gold" />
                              <span>{formatEventDate(event.eventDate)}</span>
                            </div>
                          )}
                          {event.location && (
                            <div className="flex items-center gap-1.5">
                              <MapPin className="w-4 h-4 text-gold" />
                              <span>{event.location}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-1.5">
                            <Video className="w-4 h-4 text-gold" />
                            <span>{event.videoCount} upload{event.videoCount !== 1 ? 's' : ''}</span>
                          </div>
                        </div>

                        {/* Privacy Toggle - Only show for active accounts */}
                        {event.clientSetupStatus === 'active' && (
                          <div className="mt-3 pt-3 border-t border-white/10">
                            <div className="flex flex-wrap items-center gap-2">
                              <button
                                onClick={(e) => handleTogglePrivacy(event, e)}
                                disabled={updatingPrivacy === event.id}
                                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                                  event.isPublic
                                    ? 'bg-green-500/20 text-green-400 border border-green-500/40 hover:bg-green-500/30'
                                    : 'bg-blue-500/20 text-blue-400 border border-blue-500/40 hover:bg-blue-500/30'
                                } disabled:opacity-50 disabled:cursor-not-allowed`}
                              >
                                {updatingPrivacy === event.id ? (
                                  <>
                                    <LoadingSpinner size="small" />
                                    Updating...
                                  </>
                                ) : (
                                  <>
                                    {event.isPublic ? (
                                      <>
                                        <Eye className="w-3.5 h-3.5" />
                                        Public Gallery
                                      </>
                                    ) : (
                                      <>
                                        <EyeOff className="w-3.5 h-3.5" />
                                        Private Gallery
                                      </>
                                    )}
                                  </>
                                )}
                              </button>

                              {event.isPublic && (
                                <button
                                  onClick={(e) => handleCopyPublicLink(event, e)}
                                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                                    copiedLinkEventId === event.id
                                      ? 'bg-green-500/20 border-green-500/40 text-green-400'
                                      : 'bg-white/10 border-white/20 text-white hover:bg-white/20'
                                  }`}
                                >
                                  {copiedLinkEventId === event.id ? (
                                    <Check className="w-3.5 h-3.5" />
                                  ) : (
                                    <Link2 className="w-3.5 h-3.5" />
                                  )}
                                  {copiedLinkEventId === event.id ? 'Copied' : 'Copy Link'}
                                </button>
                              )}
                            </div>
                            <p className="mt-1.5 text-xs text-gray-500">
                              {event.isPublic
                                ? 'Anyone with the link can view'
                                : 'Only you and admins can view'}
                            </p>
                          </div>
                        )}
                      </div>

                      <ArrowRight className="w-6 h-6 text-gold opacity-50 group-hover:opacity-100 group-hover:translate-x-1 transition-all shrink-0 ml-4" />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Setup Email Sent Modal */}
        {setupEmailSent && (
          <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
            <div className="bg-neutral-900 rounded-2xl max-w-md w-full p-8 border border-white/10 shadow-2xl">
              <div className="text-center">
                <div className="w-20 h-20 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Mail className="w-10 h-10 text-gold" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">
                  Check Your Email
                </h3>
                <p className="text-neutral-grey-300 mb-2">
                  We've sent a secure setup link to <strong className="text-white">{lookupEmail}</strong>.
                  Click the link in the email to create your password and access your event gallery.
                </p>
                <p className="text-neutral-grey-400 text-sm mb-6">
                  💡 Can't find it? Check your spam or junk folder.
                </p>
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-300 text-left">
                      <p className="font-semibold mb-1">Security Note</p>
                      <p>For your protection, you must set up a password before accessing your gallery.
                      The setup link will expire in 24 hours.</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-3">
                  <button
                    onClick={() => setSetupEmailSent(false)}
                    className="w-full py-3 bg-gold hover:bg-gold-light text-dark-slate font-bold rounded-lg transition-all"
                  >
                    Got It
                  </button>

                  <button
                    onClick={handleResendEmail}
                    disabled={isResending}
                    className="w-full py-2 text-gold hover:text-gold-light text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isResending ? 'Resending...' : "Didn't receive an email? Click here to resend"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ClientPortalPage;
